import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Workflow, Inbox, Building2, Save, Info, UserCheck } from "lucide-react";

export default function WhatsAppWorkflowPage() {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [receptionUserId, setReceptionUserId] = useState("");
  const [deptUserMap, setDeptUserMap] = useState({});

  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const activeUsers = usuarios
    .filter(u => u.is_active !== false)
    .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [deptRes, usersRes, wfRes] = await Promise.all([
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/workflow`, getAuthHeaders())
      ]);
      setDepartamentos(deptRes.data || []);
      setUsuarios(usersRes.data || []);
      const wf = wfRes.data || {};
      setReceptionUserId(wf.reception_user_id || "");
      const map = {};
      (wf.department_defaults || []).forEach(d => {
        if (d.department_id && d.user_id) map[d.department_id] = d.user_id;
      });
      setDeptUserMap(map);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar la configuración de Workflow");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setDeptUser = (deptId, userId) => {
    setDeptUserMap(prev => {
      const next = { ...prev };
      if (userId === "none") delete next[deptId];
      else next[deptId] = userId;
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const department_defaults = Object.entries(deptUserMap)
        .filter(([, uid]) => uid)
        .map(([department_id, user_id]) => ({ department_id, user_id }));
      await axios.put(`${API}/whatsapp/workflow`, {
        reception_user_id: receptionUserId || null,
        department_defaults
      }, getAuthHeaders());
      toast.success("Configuración de Workflow guardada");
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const userName = (u) => u.name || u.email;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="whatsapp-workflow-page">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Workflow className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Workflow de WhatsApp</h1>
              <p className="text-sm text-slate-500">Define a quién le llegan las conversaciones automáticamente</p>
            </div>
          </div>

          {!isAdmin && (
            <Card>
              <CardContent className="py-8 text-center text-slate-500">
                Solo los administradores pueden configurar el Workflow.
              </CardContent>
            </Card>
          )}

          {isAdmin && (loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Reception user */}
              <Card data-testid="reception-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Inbox className="w-5 h-5 text-green-600" />
                    Usuario de recepción (primer contacto)
                  </CardTitle>
                  <CardDescription className="flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Toda conversación NUEVA que nadie haya atendido caerá a este usuario, para que él identifique qué necesita el cliente y la reasigne. Aplica solo cuando NO hay un chatbot activo (si hay chatbot, este enruta directamente).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Label className="text-sm">Usuario</Label>
                  <Select value={receptionUserId || "none"} onValueChange={(v) => setReceptionUserId(v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="reception-user-select">
                      <SelectValue placeholder="Seleccionar usuario..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin usuario de recepción</SelectItem>
                      {activeUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{userName(u)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Department defaults */}
              <Card data-testid="dept-defaults-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Usuario por defecto por departamento
                  </CardTitle>
                  <CardDescription className="flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    Cuando una conversación se redirige a un departamento (manualmente sin elegir agente, o por palabra clave del chatbot), le llegará al usuario que definas aquí.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {departamentos.length === 0 ? (
                    <p className="text-sm text-slate-500">No hay departamentos configurados.</p>
                  ) : (
                    departamentos.map(d => (
                      <div key={d.id} className="grid grid-cols-1 sm:grid-cols-3 items-center gap-2" data-testid={`dept-row-${d.id}`}>
                        <Label className="text-sm flex items-center gap-1.5 sm:col-span-1">
                          <Building2 className="w-3.5 h-3.5 text-slate-400" />
                          {d.name}
                        </Label>
                        <div className="sm:col-span-2">
                          <Select value={deptUserMap[d.id] || "none"} onValueChange={(v) => setDeptUser(d.id, v)}>
                            <SelectTrigger data-testid={`dept-user-select-${d.id}`}>
                              <SelectValue placeholder="Sin usuario por defecto" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin usuario por defecto</SelectItem>
                              {activeUsers.map(u => (
                                <SelectItem key={u.id} value={u.id}>{userName(u)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-workflow-button">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar configuración
                </Button>
              </div>
            </>
          ))}
        </div>
      </main>
    </div>
  );
}
